// «Товар открыт из идеи» — к какой идее отнести переход в карточку и
// добавление в корзину.
//
// Кабинет продавца и консоль считают по каждой идее охват, показы, переходы в
// карточку и «в корзину» (шлюз, `services/ideas.ts`: события `product_view` и
// `cart_add` с `collection_id` идеи). Витрина же слала эти два события без
// идеи — и два последних столбца были нулями у всех.
//
// Связь «идея → товар» запоминается в момент, когда покупатель ДЕЙСТВУЕТ с
// товаром внутри идеи: открывает его по ссылке или точке на фото, смотрит
// быстрый просмотр, кладёт в корзину. Простой просмотр страницы идеи товары к
// ней не привязывает — иначе любой поздний заход на карточку из каталога
// приписался бы идее, которую человек лишь пролистал.
//
// Хранится в `sessionStorage` по id товара: событие `cart_add` отправляет
// корзина (`store/cart.ts`), которая об идее ничего не знает, — она берёт
// привязку отсюда через `trackCartAdd`. Ссылка на карточку из идеи несёт ещё и
// `?idea=<id>`: открытая в новой вкладке (где своё хранилище сессии) или
// пересланная карточка тоже засчитывается идее.
import { createContext, useContext } from "react";
import { rememberDiscoverySource } from "./discovery-source";
export type IdeaOrigin = {
  /** id подборки в графе ленты (`dcol_…`) — по нему шлюз и считает идею. */
  collectionId: string;
  collectionSlug?: string;
};
const STORAGE_KEY = "osmix-idea-origin";

/** Привязка живёт полчаса: дальше переход в карточку — уже не «из идеи». */
const TTL_MS = 30 * 60 * 1000;

/** Сколько товаров помним: хватает на любую идею и не раздувает хранилище. */
const MAX_ENTRIES = 50;

/** Параметр ссылки на карточку товара, открытой из идеи. */
export const IDEA_PARAM = "idea";
type Stored = Record<string, IdeaOrigin & {
  at: number;
}>;
const isBrowser = (): boolean => typeof window !== "undefined";
const read = (): Stored => {
  if (!isBrowser()) return {};
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) as Stored : {};
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
};

/** Запомнить, что товары открыты/положены в корзину из идеи. Last touch. */
export const rememberIdeaOrigin = (productIds: string | string[], origin: IdeaOrigin | null | undefined): void => {
  if (!isBrowser() || !origin?.collectionId) return;
  const ids = (Array.isArray(productIds) ? productIds : [productIds]).filter(Boolean);
  if (ids.length === 0) return;
  // Путь к товару — через идею: источник заказа «лента вдохновения»
  // (fd-discovery-06). Рекламный переход он не перебивает (см. discovery-source).
  rememberDiscoverySource("inspiration_feed");
  try {
    const now = Date.now();
    const next: Stored = {};
    // Просроченные записи выбрасываем, свежие переносим, новые — поверх.
    for (const [id, entry] of Object.entries(read())) {
      if (now - entry.at <= TTL_MS) next[id] = entry;
    }
    for (const id of ids) next[id] = {
      ...origin,
      at: now
    };
    const trimmed = Object.entries(next).sort((a, b) => b[1].at - a[1].at).slice(0, MAX_ENTRIES);
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(Object.fromEntries(trimmed)));
  } catch {
    // Хранилище заблокировано — события уйдут без идеи, как раньше.
  }
};

/** Из какой идеи открыт товар (если из идеи и недавно). */
export const ideaOriginFor = (productId: string): IdeaOrigin | undefined => {
  const entry = read()[productId];
  if (!entry || typeof entry.at !== "number" || Date.now() - entry.at > TTL_MS) return undefined;
  return {
    collectionId: entry.collectionId,
    collectionSlug: entry.collectionSlug
  };
};

/** Ссылка на карточку товара с пометкой идеи. */
export const productHref = (productId: string, origin?: IdeaOrigin | null): string => origin?.collectionId ? `/product/${productId}?${IDEA_PARAM}=${encodeURIComponent(origin.collectionId)}` : `/product/${productId}`;

// Контекст идеи для компонентов, которые рисуются и вне неё (быстрый просмотр,
// точки на фото, панель «Товары из идеи» в модалке избранного).
const IdeaOriginContext = createContext<IdeaOrigin | null>(null);
export const IdeaOriginProvider = IdeaOriginContext.Provider;
export const useIdeaOrigin = () => {
  return useContext(IdeaOriginContext);
};